import { I18n } from '@affine/i18n';
import type {
  GoalTreeEnvelope,
  GoalTreeModelTier,
  GoalTreeNode,
  GoalTreeNodeStatus,
  GoalTreeReasoningLevel,
} from '@streamlain/view-config';

export interface GoalTreeViewNode {
  readonly node: GoalTreeNode;
  readonly nodeId: string;
  readonly label: string;
  readonly subtitle: string;
  readonly statusLabel: string;
  readonly statusTone: string;
  readonly ariaLabel: string;
  readonly modelLabel: string;
  readonly editable: boolean;
  readonly active: boolean;
  readonly depth: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
  readonly treeGuideDepths: number[];
  readonly treeLast: boolean;
}

export interface GoalTreeViewModel {
  readonly title: string;
  readonly subtitle: string;
  readonly progressLabel: string;
  readonly nodes: readonly GoalTreeViewNode[];
  readonly gapsBlocking: number;
  readonly gapsWarning: number;
}

export function createGoalTreeViewModel(input: {
  readonly tree: GoalTreeEnvelope;
  readonly expandedNodeIds: ReadonlySet<string>;
  readonly activeNodeId?: string | null;
}): GoalTreeViewModel {
  const nodes: GoalTreeViewNode[] = [];
  collectNodes({
    node: input.tree.root,
    depth: 0,
    expandedNodeIds: input.expandedNodeIds,
    nodes,
    treeGuideDepths: [],
    treeLast: true,
    activeNodeId: input.activeNodeId ?? null,
  });
  return {
    title: input.tree.root.label,
    subtitle: goalModeLabelForDisplay(input.tree.domain, input.tree.mode),
    progressLabel: progressLabel(input.tree),
    nodes,
    gapsBlocking: input.tree.gaps.filter(gap => gap.blocking).length,
    gapsWarning: input.tree.gaps.filter(gap => !gap.blocking).length,
  };
}

export function effectiveStepModelLabel(
  tier: GoalTreeModelTier | undefined,
  reasoning: GoalTreeReasoningLevel | undefined
): string {
  const parts = [
    tier ? modelTierLabel(tier) : null,
    reasoning && reasoning !== 'off'
      ? I18n.t('com.streamlain.ai.target.model.reasoning', {
          level: reasoningLabel(reasoning),
        })
      : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function modelTierLabel(tier: GoalTreeModelTier): string {
  return {
    economy: I18n.t('com.streamlain.ai.target.model.economy'),
    standard: I18n.t('com.streamlain.ai.target.model.standard'),
    pro: I18n.t('com.streamlain.ai.target.model.pro'),
  }[tier];
}

export function goalStatusLabel(status: GoalTreeNodeStatus | string): string {
  const labels: Record<string, string> = {
    blocked: I18n.t('com.streamlain.ai.target.status.blocked'),
    completed: I18n.t('com.streamlain.ai.target.status.completed'),
    configured: I18n.t('com.streamlain.ai.target.status.configured'),
    defaulted: I18n.t('com.streamlain.ai.target.status.prepared'),
    invalid: I18n.t('com.streamlain.ai.target.status.invalid'),
    missing: I18n.t('com.streamlain.ai.target.status.missing'),
    ready: I18n.t('com.streamlain.ai.target.status.ready'),
    running: I18n.t('com.streamlain.ai.target.status.running'),
  };
  return labels[status] ?? I18n.t('com.streamlain.ai.target.status.open');
}

export function goalNodeLabel(node: GoalTreeNode): string {
  const labels: Record<string, string> = {
    'app.title': I18n.t('com.streamlain.ai.target.node.app-title'),
    'app.brief': I18n.t('com.streamlain.ai.target.node.app-brief'),
    'app.slug': I18n.t('com.streamlain.ai.target.node.app-address'),
    'app.exposure': I18n.t('com.streamlain.ai.target.node.visibility'),
    'app.exposure.delivery': I18n.t('com.streamlain.ai.target.node.delivery'),
    'app.exposure.auth': I18n.t('com.streamlain.ai.target.node.access'),
    'app.exposure.cache': I18n.t('com.streamlain.ai.target.node.performance'),
    'app.exposure.robots': I18n.t('com.streamlain.ai.target.node.discovery'),
    'app.shell': I18n.t('com.streamlain.ai.target.node.structure'),
    'app.composition': I18n.t('com.streamlain.ai.target.node.composition'),
    'app.mount': I18n.t('com.streamlain.ai.target.node.main-view'),
    'app.route': I18n.t('com.streamlain.ai.target.node.start-page'),
    'app.file': I18n.t('com.streamlain.ai.target.node.interface'),
    'app.file.html': I18n.t('com.streamlain.ai.target.node.layout'),
    'app.file.css': I18n.t('com.streamlain.ai.target.node.design'),
    'app.file.js': I18n.t('com.streamlain.ai.target.node.behavior'),
    'app.block': I18n.t('com.streamlain.ai.target.node.app-view'),
    'app.database.requirement': I18n.t('com.streamlain.ai.target.node.data'),
    'app.resource': I18n.t('com.streamlain.ai.target.node.data-source'),
    'app.column.requirement': I18n.t(
      'com.streamlain.ai.target.node.data-fields'
    ),
    'app.database.binding': I18n.t('com.streamlain.ai.target.node.database'),
    'app.automation.binding': I18n.t(
      'com.streamlain.ai.target.node.automation'
    ),
    'app.command': I18n.t('com.streamlain.ai.target.node.actions'),
    'app.export': I18n.t('com.streamlain.ai.target.node.export'),
    'app.seo': I18n.t('com.streamlain.ai.target.node.findability'),
    'app.operator.controls': I18n.t(
      'com.streamlain.ai.target.node.ai-controls'
    ),
    'app.health': I18n.t('com.streamlain.ai.target.node.quality'),
    'app.health.config': I18n.t('com.streamlain.ai.target.node.configuration'),
    'app.health.composition': I18n.t(
      'com.streamlain.ai.target.node.view-structure'
    ),
    'app.health.mainMount': I18n.t(
      'com.streamlain.ai.target.node.main-view-check'
    ),
    'app.health.artifact': I18n.t('com.streamlain.ai.target.node.app-files'),
    'app.health.databaseSchema': I18n.t(
      'com.streamlain.ai.target.node.data-structure'
    ),
    'app.health.databasePresentation': I18n.t(
      'com.streamlain.ai.target.node.data-view'
    ),
    'app.health.dataBinding': I18n.t(
      'com.streamlain.ai.target.node.data-connection'
    ),
    'app.health.interaction': I18n.t(
      'com.streamlain.ai.target.node.interaction'
    ),
    'app.health.live': I18n.t('com.streamlain.ai.target.node.publication'),
    'app.health.launch': I18n.t('com.streamlain.ai.target.node.launch'),
    'app.publish': I18n.t('com.streamlain.ai.target.node.publish'),
  };
  return labels[node.nodeId] ?? node.label;
}

export function resolveActiveGoalNodeId(
  tree: GoalTreeEnvelope,
  goalStatus?: string | null
): string | null {
  if (goalStatus !== 'queued' && goalStatus !== 'running') return null;
  return tree.nextSteps[0]?.nodeId ?? tree.root.nodeId;
}

export function goalActivityText(input: {
  tree: GoalTreeEnvelope;
  status?: string | null;
}): string | null {
  const activeNodeId = resolveActiveGoalNodeId(input.tree, input.status);
  const activeNode = activeNodeId
    ? findNode(input.tree.root, activeNodeId)
    : null;
  if (input.status === 'queued') {
    return I18n.t('com.streamlain.ai.target.activity.preparing');
  }
  if (input.status === 'running') {
    return I18n.t('com.streamlain.ai.target.activity.working', {
      step: activeNode ? goalNodeLabel(activeNode) : input.tree.root.label,
    });
  }
  if (input.status === 'waiting_for_confirmation') {
    return I18n.t('com.streamlain.ai.target.activity.confirmation');
  }
  if (input.status === 'waiting_for_user') {
    return I18n.t('com.streamlain.ai.target.activity.input');
  }
  if (input.status === 'paused') {
    return I18n.t('com.streamlain.ai.target.activity.paused');
  }
  return null;
}

export function nodeStatusTone(status: GoalTreeNodeStatus) {
  if (status === 'completed' || status === 'configured') return 'positive';
  if (status === 'invalid') return 'critical';
  if (status === 'blocked') return 'blocked';
  if (status === 'missing') return 'warning';
  if (status === 'running') return 'accent';
  if (status === 'defaulted') return 'muted';
  return 'neutral';
}

function collectNodes(input: {
  readonly node: GoalTreeNode;
  readonly depth: number;
  readonly expandedNodeIds: ReadonlySet<string>;
  readonly nodes: GoalTreeViewNode[];
  readonly treeGuideDepths: readonly number[];
  readonly treeLast: boolean;
  readonly activeNodeId: string | null;
}): void {
  const hasChildren = input.node.children.length > 0;
  const expanded =
    !hasChildren ||
    input.depth < 1 ||
    input.expandedNodeIds.has(input.node.nodeId);
  input.nodes.push({
    node: input.node,
    nodeId: input.node.nodeId,
    label: goalNodeLabel(input.node),
    subtitle: goalNodeSubtitle(input.node),
    statusLabel: goalStatusLabel(input.node.status),
    statusTone: nodeStatusTone(input.node.status),
    ariaLabel: `${goalNodeLabel(input.node)}, ${goalStatusLabel(input.node.status)}`,
    modelLabel: effectiveStepModelLabel(
      input.node.modelPolicy?.userOverride?.tier ??
        input.node.modelPolicy?.defaultTier,
      input.node.modelPolicy?.userOverride?.reasoning ??
        input.node.modelPolicy?.reasoning
    ),
    editable:
      input.node.required &&
      (input.node.status === 'missing' ||
        input.node.status === 'invalid' ||
        input.node.status === 'blocked'),
    active: input.node.nodeId === input.activeNodeId,
    depth: input.depth,
    hasChildren,
    expanded,
    treeGuideDepths: [...input.treeGuideDepths],
    treeLast: input.treeLast,
  });
  if (!expanded) return;
  input.node.children.forEach((child, index, siblings) => {
    collectNodes({
      node: child,
      depth: input.depth + 1,
      expandedNodeIds: input.expandedNodeIds,
      nodes: input.nodes,
      treeGuideDepths:
        input.treeLast || input.depth === 0
          ? input.treeGuideDepths
          : [...input.treeGuideDepths, input.depth],
      treeLast: index === siblings.length - 1,
      activeNodeId: input.activeNodeId,
    });
  });
}

function goalNodeSubtitle(node: GoalTreeNode): string {
  if (
    (node.nodeId === 'app.title' || node.nodeId === 'app.brief') &&
    node.valueSummary
  ) {
    return node.valueSummary;
  }
  return goalStatusLabel(node.status);
}

export function goalModeLabelForDisplay(domain: string, mode: string): string {
  if (domain === 'custom_app') {
    return mode === 'create_app_from_scratch'
      ? I18n.t('com.streamlain.ai.target.mode.create-app')
      : I18n.t('com.streamlain.ai.target.mode.edit-app');
  }
  if (domain === 'automation') {
    return I18n.t('com.streamlain.ai.target.mode.automation');
  }
  return I18n.t('com.streamlain.ai.target.mode.general');
}

export function reasoningLabel(reasoning: GoalTreeReasoningLevel): string {
  return {
    off: I18n.t('com.streamlain.ai.target.reasoning.off'),
    low: I18n.t('com.streamlain.ai.target.reasoning.low'),
    medium: I18n.t('com.streamlain.ai.target.reasoning.medium'),
    high: I18n.t('com.streamlain.ai.target.reasoning.high'),
  }[reasoning];
}

function findNode(node: GoalTreeNode, nodeId: string): GoalTreeNode | null {
  if (node.nodeId === nodeId) return node;
  for (const child of node.children) {
    const match = findNode(child, nodeId);
    if (match) return match;
  }
  return null;
}

function progressLabel(tree: GoalTreeEnvelope): string {
  const done =
    (tree.progress.completed ?? 0) +
    tree.progress.ready +
    tree.progress.configured +
    tree.progress.defaulted;
  return `${done}/${tree.progress.total}`;
}
